import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';

type NumberFieldProps = {
  field: { label?: string };
  name: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

/**
 * Returns a Puck custom field render function for numeric CSS values.
 * The `unit` parameter (e.g. 'px', 'rem') is shown as a suffix badge
 * and appended to the stored value.
 */
export function NumberField(unit: string) {
  return function NumberFieldWidget({ field, name, value, onChange, readOnly }: NumberFieldProps): JSX.Element {
    const numericValue = parseFloat(value) || 0;

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.currentTarget.value;
        onChange(raw === '' ? `0${unit}` : `${raw}${unit}`);
      },
      [onChange]
    );

    return (
      <FieldLabel label={field.label ?? name} readOnly={readOnly}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type='number'
            value={numericValue}
            onChange={handleChange}
            disabled={readOnly}
            min={0}
            step={1}
            data-testid={`number-input-${name}`}
            style={{
              flex: 1,
              // Without this the input's intrinsic width wins inside a narrow
              // container (e.g. an `object` field group) and clips the unit badge.
              minWidth: 0,
              padding: '6px 8px',
              border: '1px solid #d0d4d8',
              borderRadius: 4,
              fontSize: 14,
            }}
          />
          {unit && (
            <span
              data-testid={`number-unit-${name}`}
              style={{
                padding: '6px 8px',
                background: '#f0f2f4',
                borderRadius: 4,
                fontSize: 12,
                color: '#646d72',
                fontWeight: 500,
              }}
            >
              {unit}
            </span>
          )}
        </div>
      </FieldLabel>
    );
  };
}
